import { glsGetOidFromToken } from '@gls-its-frontends/common/gls-helpers';
import { defineStore } from 'pinia';
import { computed, ComputedRef, type Ref, ref, watch } from 'vue';
import { CONTACT_ID_WILDCARD } from '@/constants/contact-id-wildcard';
import { Permission } from '@/enums/permission';
import { Role } from '@/enums/role';
import { AttributeStoreUserData, RolesBySystem, UserShipper } from '@/types/api/attribute-store';
import { System } from '@/types/api/enums/system';

const mainStoreKey: string = 'main';

const mainStore = defineStore(mainStoreKey, () => {
    const accessToken: Ref<string | null> = ref(null);
    const attributeStoreData: Ref<AttributeStoreUserData | null> = ref(null);
    const username: Ref<string | null> = ref(null);
    const permissions: Ref<Permission[]> = ref([]);
    const oid: ComputedRef<string | null> = computed((): string | null => glsGetOidFromToken(accessToken.value));
    const roles: ComputedRef<Role[]> = computed((): Role[] => {
        const webRoles: RolesBySystem | undefined = attributeStoreData.value?.rolesBySystem.find(
            (rbs: RolesBySystem): boolean => rbs.system === System.WEB
        );

        return webRoles?.roles || [];
    });

    const shippers: ComputedRef<UserShipper[]> = computed((): UserShipper[] => {
        return attributeStoreData.value?.shippers || [];
    });

    const activeShippers: ComputedRef<UserShipper[]> = computed((): UserShipper[] => {
        return shippers.value.filter((shipper: UserShipper): boolean => shipper.isActive && shipper.contactId !== CONTACT_ID_WILDCARD);
    });

    const setAccessToken = (token: string): void => {
        accessToken.value = token;
    };

    const setUsername = (name: string): void => {
        username.value = name;
    };

    const setPermissions = (userPermissions?: Permission[]): void => {
        permissions.value = userPermissions?.length ? [...userPermissions] : [];
    };

    watch(oid, (newOid: string | null): void => {
        if (newOid === attributeStoreData.value?.oid) {
            return;
        }

        attributeStoreData.value = null;
    });

    return {
        accessToken,
        setAccessToken,
        attributeStoreData,
        oid,
        roles,
        setUsername,
        username,
        permissions,
        setPermissions,
        shippers,
        activeShippers
    };
});

export default mainStore;
